<template>
    <el-form id="tenant-form" data-cy="tenant-form" label-position="top" :rules="rules" :model="formFields" ref="form">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />

        <!-- Customer Type and Template Selection -->
        <div class="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h3 class="text-lg font-medium mb-4">Template Setup</h3>

            <div class="space-y-4">
                <el-form-item label="Customer Type" prop="customerType">
                    <el-select data-cy="customer-type-top" class="w-full" v-model="formFields.customerType"
                        placeholder="Select a customer type" @change="onCustomerTypeChange" clearable>
                        <el-option v-for="(item, key) in getListCustomerType" :key="key" :label="item" :value="item" />
                    </el-select>
                </el-form-item>

                <el-form-item label="Template" prop="selectedTemplate" v-if="formFields.customerType">
                    <!-- Loading state -->
                    <div v-if="loadingTemplates" class="flex items-center justify-center py-4">
                        <i class="el-icon-loading mr-2"></i>
                        <span class="text-gray-600">Loading templates...</span>
                    </div>

                    <!-- Templates available -->
                    <div v-else-if="templateTenants.length > 0">
                        <el-select data-cy="template-select" class="w-full" v-model="formFields.templateTenantId"
                            placeholder="Select a template (optional)" filterable clearable>
                            <el-option v-for="template in templateTenants" :key="template.id"
                                :label="template.companyName" :value="template.id" />
                        </el-select>
                    </div>

                    <!-- No templates available -->
                    <div v-else>
                        <p class="text-gray-500 text-sm">No templates available for this customer type</p>
                    </div>
                </el-form-item>
            </div>
        </div>

        <el-collapse>
            <el-collapse-item title="Basic Info" name="1">
                <el-form-item label="Company Name" prop="companyName">
                    <el-input data-cy="company-name" v-model="formFields.companyName"
                        :disabled="!$store.getters.isSystemAdmin"></el-input>
                </el-form-item>
                <div class="grid grid-cols-2 gap-2">
                    <el-form-item label="Timezone" prop="timeZone">
                        <TimeZoneSelect data-cy="timezone" v-model="formFields.timeZone" class="" />
                    </el-form-item>
                </div>

            </el-collapse-item>

            <el-collapse-item title="Settings" name="3">

                <div class="grid grid-cols-2 gap-2">
                    <el-form-item label="Trial Expiration Date" prop="trialExpDate">
                        <el-date-picker data-cy="trial-exp-date" type="date" placeholder="Select a day"
                            format="MM/dd/yyyy" v-model="formFields.trialExpDate" style="width:100%">
                        </el-date-picker>
                    </el-form-item>
                </div>
                <div class="grid grid-cols-1 gap-2">
                    <el-form-item label="Customer Sub Type" prop="customerSubType">
                        <el-select data-cy="customer-subtype" class="w-full" v-model="formFields.customerSubType"
                            placeholder="Select a customer sub type">
                            <el-option v-for="(item, key) in listCustomerSubType" :key="key" :label="item"
                                :value="item" />
                        </el-select>
                    </el-form-item>
                </div>
                <div class="grid grid-cols-2 gap-2">
                    <el-form-item label="Account Country" prop="accountCountry">
                        <el-select data-cy="account-country" class="w-full" v-model="formFields.accountCountry"
                            placeholder="Select a account country">
                            <el-option v-for="(item, key) in getListAccountCountry" :key="key" :label="item"
                                :value="item" />
                        </el-select>
                    </el-form-item>

                </div>
            </el-collapse-item>

        </el-collapse>
    </el-form>
</template>

<script>
import TimeZoneSelect from './../../../Settings/CompanyDetails/components/TimeZoneSelect.vue'
import {  listTemplateTenants } from '../queries';
import DatePicker from '@/components/Datepicker';

import {
    CUSTOMER_TYPE,
    CUSTOMER_SUBTYPE,
    ACCOUNT_COUNTRY,
    CUSTOMER_TYPE_SUBTYPE_AMAZON_DSP,
    CUSTOMER_TYPE_SUBTYPE_FEDEX_ISP,
    CUSTOMER_TYPE_AMAZON_DSP,
} from '../constants';

export default {
    components: { TimeZoneSelect, DatePicker },
    props: ['formFields', 'servicesMessage'],
    data() {
        return {
            tenantData: {},
            prevTimeZone: '',
            listCustomerSubType: CUSTOMER_SUBTYPE,
            templateTenants: [],
            loadingTemplates: false,
            rules: {
                companyName: [
                    { required: true, message: "Please introduce a company name", trigger: 'blur' }
                ],
                timeZone: [
                    { required: true, message: "Please select a timezone", trigger: 'change' }
                ],
                trialExpDate: [
                    { required: true, message: "Please select a expiration date", trigger: 'change' },
                    {
                        validator: (rule, value, callback) => {
                            if (value && new Date(value) <= new Date()) {
                                callback(new Error('Trial Expiration date must be in the future'));
                            } else {
                                callback();
                            }
                        }, trigger: 'change'
                    }
                ],
                customerType: [
                    { required: true, message: "Please select a customer type", trigger: 'change' }
                ],
                customerSubType: [
                    { required: true, message: "Please select a customer sub type", trigger: 'change' }
                ],
                accountCountry: [
                    { required: true, message: "Please select a account country", trigger: 'change' }
                ],
            },
        }
    },
    watch: {
        'formFields.customerType'(newValue, oldValue) {
            // If customer type changes and it's different from the old value
            if (newValue !== oldValue && oldValue !== undefined) {
                this.onCustomerTypeChange(newValue);
            }
        }
    },
    computed: {

        getListCustomerType(){
            return CUSTOMER_TYPE || []
        },
        getListAccountCountry(){
            return ACCOUNT_COUNTRY || []
        },
    },
    methods: {
        async onCustomerTypeChange(customerType) {
            this.formFields.templateTenantId = null;
            this.templateTenants = []; // Clear previous templates
            this.loadingTemplates = true; // Start loading
            
            if (customerType) {
                await this.loadTemplateTenants();
                this.changeCustomerType(customerType);
            }
            
            this.loadingTemplates = false; // Stop loading
        },
        
        async loadTemplateTenants() {
            try {
                const result = await this.gLoadListAll(listTemplateTenants, { 
                    filter: { 
                        isTemplate: { eq: true },
                        customerType: { eq: this.formFields.customerType }
                    }
                }, 'listTenants');
                this.templateTenants = result || [];
            } catch (error) {
                console.error('Error loading template tenants:', error);
                this.templateTenants = [];
            }
        },
        
        handleDateSelected(date, model) {
            this.formFields[model] = date
        },
        changeCustomerType(e){
            this.listCustomerSubType = (e === CUSTOMER_TYPE_AMAZON_DSP) ? CUSTOMER_TYPE_SUBTYPE_AMAZON_DSP : CUSTOMER_TYPE_SUBTYPE_FEDEX_ISP
        },
    },
    mounted() {
        if (this.formFields.customerType) {
            this.loadTemplateTenants();
        }
    }
}
</script>
<style>
@media screen and (max-width: 768px) {
    .el-dialog[aria-label="Edit Tenant"] {
        width: 90% !important;
    }

    #edit-tenant .el-dialog__body {
        padding-top: 0;
    }
}

#tenant-form .el-collapse-item {
    padding: 0;
    margin-bottom: 0 !important;
    border: 0 !important;
    box-shadow: none !important;
}

#tenant-form .el-collapse-item div {
    border: 0 !important;
    box-shadow: none !important;
}

</style>